import React, { useEffect, useState, useRef } from 'react';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';
import ImageUpload from '../components/ImageUpload';
import { listDocs, createDoc, updateDocById, deleteDocById } from '../services/dbService';
import toast from 'react-hot-toast';
import './Categories.css';

const Categories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({ title: '', description: '', imageUrl: '' });
    const imageUploadRef = useRef(null);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const docs = await listDocs('eventtypes');
            setCategories(docs.map((d) => ({ 
                id: d.id, 
                title: d.title || d.name || '', 
                category: d.title || d.name || '', 
                description: d.description || '', 
                imageUrl: d.imageUrl || '',
                createdAt: d.created_at || d.createdAt || null
            })));
        } catch (err) {
            console.error(err);
            toast.error('Failed to fetch categories');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (category = null) => {
        if (category) {
            setEditingCategory(category);
            setFormData({ title: category.title, description: category.description, imageUrl: category.imageUrl });
        } else {
            setEditingCategory(null);
            setFormData({ title: '', description: '', imageUrl: '' });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingCategory(null);
        setFormData({ title: '', description: '', imageUrl: '' });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Ensure pending image is uploaded
            if (imageUploadRef.current && imageUploadRef.current.hasFile && imageUploadRef.current.hasFile()) {
                try {
                    const uploadedUrl = await imageUploadRef.current.uploadIfPending();
                    if (uploadedUrl) setFormData((prev) => ({ ...prev, imageUrl: uploadedUrl }));
                } catch (uErr) {
                    console.error('Image upload failed:', uErr);
                    toast.error('Image upload failed. Please try again.');
                    return;
                }
            }

            if (editingCategory) {
                await updateDocById('eventtypes', editingCategory.id || editingCategory._id, { 
                    title: formData.title, 
                    name: formData.title, 
                    description: formData.description, 
                    imageUrl: formData.imageUrl 
                });
                toast.success('Category updated successfully');
            } else {
                // Image is optional for testing
                await createDoc('eventtypes', { 
                    title: formData.title, 
                    name: formData.title, // sync name and title
                    description: formData.description, 
                    imageUrl: formData.imageUrl, 
                    created_at: new Date().toISOString() 
                });
                toast.success('Category created successfully');
            }
            handleCloseModal();
            fetchCategories();
        } catch (err) {
            console.error(err);
            toast.error('Failed to save category');
        }
    };

    const handleDelete = async (categoryId) => {
        if (window.confirm('Are you sure you want to delete this category?')) {
            try {
                await deleteDocById('eventtypes', categoryId);
                toast.success('Category deleted successfully');
                fetchCategories();
            } catch (err) {
                console.error(err);
                toast.error('Failed to delete category');
            }
        }
    };

    if (loading) {
        return (
            <div className="categories-container">
                <p>Loading categories...</p>
            </div>
        );
    }

    return (
        <div className="categories-container">
            <div className="categories-header">
                <h1>Event Categories</h1>
                <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                    <FiPlus /> Add Category
                </button>
            </div>

            <div className="categories-grid">
                {categories.length > 0 ? (
                    categories.map((category) => (
                        <div key={category.id || category._id} className="category-card">
                            <div className="category-content">
                                <h3>{category.title}</h3>
                                <p>{category.description}</p>
                                {category.imageUrl && (
                                    <div className="category-image">
                                        <img src={category.imageUrl} alt={category.title} style={{ width: '100%', maxHeight: 160, objectFit: 'cover' }} />
                                    </div>
                                )}
                            </div>
                            <div className="category-actions">
                                <button className="btn btn-sm btn-info" onClick={() => handleOpenModal(category)}>
                                    <FiEdit2 /> Edit
                                </button>
                                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(category.id || category._id)}>
                                    <FiTrash2 /> Delete
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="no-categories">
                        <p>No categories found. Create your first category!</p>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingCategory ? 'Edit Category' : 'Add New Category'}</h2>
                            <button className="modal-close" onClick={handleCloseModal}>
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="category-form">
                            <div className="form-group">
                                <label>Category Name *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Wedding, Corporate Event, Birthday"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Description *</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="Describe this category..."
                                    required
                                    rows="4"
                                />
                            </div>

                            <div className="form-group">
                                <label>Image</label>
                                <ImageUpload
                                    ref={imageUploadRef}
                                    initialUrl={formData.imageUrl}
                                    autoUpload={true}
                                    path={`eventTypes/${editingCategory ? editingCategory.id : 'eventTypes'}`}
                                    onUpload={(url) => setFormData((prev) => ({ ...prev, imageUrl: url }))}
                                />
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary">
                                    {editingCategory ? 'Update' : 'Create'} Category
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Categories;